<script setup lang="ts">
import { Plus, Xmark } from "@iconoir/vue";
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxViewport,
  TagsInputInput,
  TagsInputItem,
  TagsInputItemDelete,
  TagsInputItemText,
  TagsInputRoot,
} from "reka-ui";
import { useGetAllTags } from "./housesCrud";
import type { Tag } from "./types";
import { useTagSuggestionFilter } from "./useTagSuggestionFilter";

const { modelValue: selectedTags, id } = defineProps<{
  modelValue: Array<Tag>;
  id: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: Array<Tag>];
}>();

const { tags: availableTags } = useGetAllTags();

const createTag = (name: string): Tag => ({
  id: globalThis.crypto.randomUUID(),
  name,
});

const { query, filteredTags, newTag } = useTagSuggestionFilter(
  availableTags,
  () => selectedTags,
  createTag,
);

const onUpdate = (value: Tag | Array<Tag>) => {
  // Just for TS: With `multiple`, ComboboxRoot always emits an array at runtime,
  // but Reka's emit type can't express that and reports the single-item type.
  const values = Array.isArray(value) ? value : [value];
  emit("update:modelValue", values);
  query.value = "";
};

// Reka skips its input handler while an IME composition is active.
// Mobile keyboards keep the composition open for the whole word,
// so `query` would only update on compositionend. We update it
// ourselves on every input; setting it to the current value keeps the bound
// `:value` identical, so desktop IME composition is left untouched.
const onSearchInput = (event: Event) => {
  query.value = (event.target as HTMLInputElement).value;
};

const onInputKeydown = (event: KeyboardEvent) => {
  if (event.key === "Enter") {
    // stop the TagsInputInput component from adding a tag on enter
    event.preventDefault();
    // only add a tag if it's not a duplicate
    if (!newTag.value) return;

    const values = [...selectedTags, newTag.value];
    onUpdate(values);
  }
};
</script>

<template>
  <ComboboxRoot
    ref="combobox"
    :model-value="selectedTags"
    multiple
    open-on-click
    ignore-filter
    @update:model-value="onUpdate"
  >
    <ComboboxAnchor as-child>
      <TagsInputRoot
        :model-value="selectedTags"
        :display-value="(tag) => tag.name"
        :convert-value="createTag"
        :add-on-blur="false"
        :add-on-tab="false"
        class="tag-input__tags"
        @update:model-value="onUpdate"
      >
        <TagsInputItem
          v-for="tag in selectedTags"
          :key="tag.id"
          :value="tag"
          class="tag-input__chip"
        >
          <TagsInputItemText />
          <TagsInputItemDelete class="tag-input__chip-delete">
            <Xmark aria-label="Entfernen" />
          </TagsInputItemDelete>
        </TagsInputItem>

        <ComboboxInput :model-value="query" as-child>
          <TagsInputInput
            v-bind="{ id, onInput: onSearchInput, onKeydown: onInputKeydown }"
            class="tag-input__input-field"
          />
        </ComboboxInput>
      </TagsInputRoot>
    </ComboboxAnchor>

    <ComboboxPortal>
      <ComboboxContent position="popper" class="tag-input__content">
        <ComboboxViewport>
          <ComboboxEmpty class="tag-input__empty">
            Keine weiteren Einträge gefunden.
          </ComboboxEmpty>
          <ComboboxItem
            v-if="newTag"
            :key="newTag.id"
            :value="newTag"
            class="tag-input__option"
          >
            <Plus aria-label="Hinzufügen" />
            {{ newTag.name }}
          </ComboboxItem>
          <ComboboxItem
            v-for="tag in filteredTags"
            :key="tag.id"
            :value="tag"
            class="tag-input__option"
          >
            {{ tag.name }}
          </ComboboxItem>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<style scoped>
.tag-input__tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
  padding: var(--padding-input);
  background-color: var(--bg-color-input);
  border-radius: var(--border-radius-input);

  /* XXX: put something here until we have consistent outline styles everywhere */
  &:focus-within,
  &:has([aria-expanded="true"]) {
    outline: 1px solid var(--color-primary);
  }
}

.tag-input__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding-inline: var(--spacing-xs);
  border-radius: var(--border-radius-input);
  background-color: var(--color-primary);
  color: var(--text-color-on-primary);
}

/* XXX: remove these overrides once the button is a vue component and global styles are removed */
.tag-input__chip-delete {
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

/* XXX: remove these overrides once the button is a vue component and global styles are removed */
.tag-input__input-field {
  flex: 1;
  min-inline-size: 8ch;
  padding: 0;
  background: transparent;
  outline: none;
  border-radius: 0;
}

:deep(.tag-input__content) {
  inline-size: var(--reka-combobox-trigger-width);
  max-block-size: 11rem;
  overflow-y: auto;
  margin-block-start: var(--spacing-xs);
  padding: var(--spacing-xs);
  background-color: var(--bg-color-card);
  border-radius: var(--border-radius-input);
  box-shadow: var(--shadow);
}

:deep(.tag-input__option) {
  display: flex;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  cursor: pointer;

  &[data-highlighted] {
    background-color: var(--grey-50);
  }
}

:deep(.tag-input__empty) {
  padding: var(--spacing-xs) var(--spacing-sm);
  color: var(--text-color-muted);
}
</style>
